import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { fetchCompany } from '@/api/v4/organization.api';
import { QueryKey } from '@/config/query-client';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import { Button } from '@/ui/button/button';

/**
 * Probes share-scoped general company access without navigating to /404.
 * Used only on the Spotlight company route for the optional Open company view.
 */
export const SpotlightOpenCompanyViewLink = () => {
  const { companyId } = useParams();
  const { t } = useTranslation('spotlight');
  const navigateTo = useNavigateTo();
  const { hasAtLeastOnePermission } = useUserPermissions();
  const canViewCompanyHome = hasAtLeastOnePermission([
    PERMISSION.VIEW_ORGANIZATION_HOME_SCREEN,
  ]);

  const { isSuccess } = useQuery({
    queryKey: [QueryKey.Company, 'share-access-probe', companyId],
    queryFn: () => (companyId ? fetchCompany(companyId) : undefined),
    enabled: Boolean(companyId) && canViewCompanyHome,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  if (!companyId || !isSuccess) {
    return null;
  }

  return (
    <Button
      data-testid="spotlight-go-to-company-page"
      onClick={() => navigateTo.company({ companyId })}
      variant="primary"
    >
      {t('goToCompanyPageButton')}
    </Button>
  );
};
